import React, { useState } from 'react'
import { useUSPhoneValidation } from '../../hook/useUSPhoneValidation';
import { formatUSPhone, handleUSPhoneChange } from '../../utils/phoneFormatter';
import useAuth from '../../hook/useAuth';
import { post_Member } from '../../services/Member';
import baseURL from '../../config/api';
import Cookies from 'js-cookie';
import useConfirm from '../../hook/useConfirm';
import { put_FamilyMember } from '../../services/FamilyMember';
import LocationFields from '../../components/LocationFields';

type Props = {
    open: boolean;
    onClose: () => void;
    familyMember: any
}

const AddFamily = ({
    open,
    onClose,
    familyMember
}: Props) => {
    const token = Cookies.get("token-my-cdir");
    const expirationDate = Cookies.get("expires-my-cdir");

    const { auth, setAuth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const [userData, setUserData] = useState<any>({
        userName: "",
        familyName: "",
        firstName: "",
        lastName: "",
        emailAddress: "",
        phoneNumber: "",
        birthday: "",
        imagePath: "",
        street: "",
        street2: "",
        city: "",
        state: "",
        country: "United States",
        zip: "",
    });
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const {
        isValid: isPhoneValid,
        touched: phoneTouched,
        setTouched: setPhoneTouched
    } = useUSPhoneValidation(userData.phoneNumber);

    const getCookieExpirationDate = () => {
        if (!expirationDate) {
            return new Date(Date.now() + 60 * 60 * 1000);
        }

        const date = new Date(expirationDate);

        return isNaN(date.getTime())
            ? new Date(Date.now() + 60 * 60 * 1000)
            : date;
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isProcessing) {
            setIsProcessing(true);

            post_Member({
                ...userData,
                imagePath: userData?.imagePath?.replace(`${baseURL}commonFile/Get?name=`, ""),
                groupId: familyMember.groupId
            }, token!.toString())
                .then((res) => {
                    updateTheFamilyMember(res);
                })
                .catch(error => {
                    setIsProcessing(false);
                    console.error(error);
                });
        }
    }

    const updateTheFamilyMember = (memberId: number) => {
        put_FamilyMember({
            ...familyMember,
            memberId
        }, token!.toString())
            .then(() => {
                setAuth({
                    ...auth,
                    memberId
                });
                Cookies.set("memberId-my-cdir", memberId?.toString(), {
                    expires: getCookieExpirationDate()
                });
                setIsProcessing(false);
                onClose();
            })
            .catch(error => {
                const errorMessage = error?.response?.data || error?.message || "Something went wrong, Please try again.";
                setConfirmData({
                    title: 'Warning',
                    summary: errorMessage,
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });
                setConfirmPopup(true);
                setIsProcessing(false);
                console.error(error);
            });
    }

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-2xl">
                <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
                    <div>
                        <h2 className="text-xl font-semibold text-secondary">
                            Create Family
                        </h2>

                        <p className="text-sm text-gray-500 mt-1">
                            Enter the family information below.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => onClose()}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xl text-gray-500 hover:bg-gray-200"
                    >
                        ×
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-6"
                >
                    <div>
                        <label className="font-semibold text-sm">
                            Family Name
                        </label>

                        <input
                            type="text"
                            placeholder="Enter Family Name"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.familyName}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    familyName: e.target.value
                                })
                            }
                            required
                        />
                    </div>

                    <br className='md:block hidden' />

                    <div>
                        <label className="font-semibold text-sm">
                            First Name
                        </label>

                        <input
                            type="text"
                            placeholder="Enter First Name"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.firstName}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    firstName: e.target.value
                                })
                            }
                            required
                        />
                    </div>

                    <div>
                        <label className="font-semibold text-sm">
                            Last Name
                        </label>

                        <input
                            type="text"
                            placeholder="Enter Last Name"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.lastName}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    lastName: e.target.value
                                })
                            }
                            required
                        />
                    </div>

                    <div>
                        <label className="font-semibold text-sm">
                            Email
                        </label>

                        <input
                            type="email"
                            placeholder="Email"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.emailAddress}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    emailAddress: e.target.value
                                })
                            }
                            required
                        />
                    </div>

                    <div>
                        <label className="font-semibold text-sm">
                            Phone Number
                        </label>

                        <input
                            type="tel"
                            name="phone"
                            value={formatUSPhone(userData.phoneNumber)}
                            onChange={(e) => {
                                setPhoneTouched(true);

                                handleUSPhoneChange(e, (digits) =>
                                    setUserData((prev: any) => ({
                                        ...prev,
                                        phoneNumber: digits,
                                    }))
                                );
                            }}
                            required
                            className="w-full rounded py-2.5 px-4 border-2 mt-2 text-sm text-black outline-secondary"
                            placeholder="Phone No."
                        />

                        {!isPhoneValid && phoneTouched && (
                            <p className="text-xs text-red-500 mt-1">
                                Please enter a valid 10-digit phone number
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold text-sm">
                            Street
                        </label>

                        <input
                            type="text"
                            placeholder="Street"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.street}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    street: e.target.value
                                })
                            }
                            required
                        />
                    </div>

                    {/* Street 2 */}
                    <div>
                        <label className="font-semibold text-sm">
                            Street 2
                        </label>

                        <input
                            type="text"
                            placeholder="Street 2"
                            className="w-full rounded py-2.5 px-4 mt-2 border-2 text-sm text-black outline-secondary"
                            value={userData.street2}
                            onChange={(e) =>
                                setUserData({
                                    ...userData,
                                    street2: e.target.value
                                })
                            }
                        />
                    </div>

                    <LocationFields
                        userData={userData}
                        setUserData={setUserData}
                    />

                    <div className="col-span-1 sm:col-span-2 flex justify-end gap-4 border-t pt-6 mt-2">
                        <button
                            type="button"
                            onClick={() => onClose()}
                            className="px-6 py-2 rounded text-black text-sm font-semibold bg-gray-200 hover:bg-gray-300"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={isProcessing}
                            className="px-6 py-2 min-w-[150px] rounded text-white text-sm font-semibold bg-secondary hover:bg-custom-blue disabled:opacity-50"
                        >
                            {isProcessing ? "Creating..." : "Create Family"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddFamily
